import React, { useState, useEffect } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import PricingCard from "../components/PricingCard";
import { Server, Cpu, Zap, Shield, ArrowRight, Check } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function GpuCloud() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API}/plans/category/gpu`);
        setPlans(res.data);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetch();
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="page-enter pt-24 pb-16">
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden"
        style={{ background: `radial-gradient(ellipse at 50% 0%, rgba(0, 243, 255, 0.1) 0%, transparent 60%), #030305` }}>
        <div className="animated-grid absolute inset-0 opacity-30" />
        <div className="max-w-5xl mx-auto px-6 text-center relative z-10">
          <div className="inline-flex items-center gap-2 mb-6 px-4 py-2 rounded-full text-xs font-rajdhani font-700 tracking-widest"
            style={{ background: `rgba(0, 243, 255, 0.1)`, border: `1px solid rgba(0, 243, 255, 0.3)`, color: "#00F3FF" }}>
            <Cpu size={12} /> NEXT-GENERATION AI INFRASTRUCTURE
          </div>
          <h1 className="font-orbitron font-900 text-4xl lg:text-5xl text-white mb-6">
            Train on <span style={{ color: "#BC13FE" }}>H200</span>. Scale on <span style={{ color: "#00FF94" }}>RTX A6000 Pro</span>.
          </h1>
          <p className="font-outfit text-lg text-gray-300 max-w-3xl mx-auto mb-10 leading-relaxed" style={{ fontWeight: 300 }}>
            INDIACLOUD.AI introduces next-generation AI infrastructure designed for startups, AI platforms, SaaS companies, and enterprise GPU workloads. Build scalable AI platforms without hyperscaler pricing.
          </p>
          <div className="flex items-center justify-center gap-6 flex-wrap">
            {["Reduce GPU Costs", "H200 Capabilities", "Instant Deployment", "No Oversubscription"].map((badge) => (
              <div key={badge} className="flex items-center gap-1.5 text-sm font-outfit text-gray-400">
                <Check size={14} color="#00FF94" /> {badge}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why This Matters / Architecture */}
      <section className="py-16" style={{ background: "rgba(10,10,18,0.4)" }}>
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="font-orbitron font-700 text-3xl text-white mb-4">
              Hybrid AI <span className="text-gradient">Cloud Architecture</span>
            </h2>
            <p className="font-outfit text-gray-400 max-w-2xl mx-auto">
              Most AI companies overspend by running inference workloads on expensive H100/H200 infrastructure. Our hybrid model creates a powerful balance between Performance, Cost Efficiency, and Scalability.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <div className="glass rounded-2xl p-8 border border-[#BC13FE]/30">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-6" style={{ background: "rgba(188,19,254,0.1)" }}>
                <Zap size={24} color="#BC13FE" />
              </div>
              <h3 className="font-orbitron font-700 text-xl text-white mb-4">Use H200 for:</h3>
              <ul className="space-y-3 font-outfit text-gray-300">
                <li className="flex items-start gap-2"><Check size={18} color="#BC13FE" className="mt-0.5 shrink-0" /> Model Training</li>
                <li className="flex items-start gap-2"><Check size={18} color="#BC13FE" className="mt-0.5 shrink-0" /> Fine Tuning</li>
                <li className="flex items-start gap-2"><Check size={18} color="#BC13FE" className="mt-0.5 shrink-0" /> HPC Workloads</li>
                <li className="flex items-start gap-2"><Check size={18} color="#BC13FE" className="mt-0.5 shrink-0" /> Large Dataset Processing</li>
              </ul>
            </div>
            <div className="glass rounded-2xl p-8 border border-[#00FF94]/30">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-6" style={{ background: "rgba(0,255,148,0.1)" }}>
                <Server size={24} color="#00FF94" />
              </div>
              <h3 className="font-orbitron font-700 text-xl text-white mb-4">Shift to RTX A6000 Pro for:</h3>
              <ul className="space-y-3 font-outfit text-gray-300">
                <li className="flex items-start gap-2"><Check size={18} color="#00FF94" className="mt-0.5 shrink-0" /> AI Inference</li>
                <li className="flex items-start gap-2"><Check size={18} color="#00FF94" className="mt-0.5 shrink-0" /> Production APIs</li>
                <li className="flex items-start gap-2"><Check size={18} color="#00FF94" className="mt-0.5 shrink-0" /> Stable Diffusion</li>
                <li className="flex items-start gap-2"><Check size={18} color="#00FF94" className="mt-0.5 shrink-0" /> Video & Enterprise AI Apps</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* GPU Plans Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="font-orbitron font-700 text-3xl text-white">GPU <span className="text-gradient">Instances</span></h2>
            <p className="font-outfit text-gray-400 mt-2">Deploy powerful, dedicated GPU servers in minutes.</p>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="text-center">
                <div className="w-12 h-12 rounded-xl mx-auto mb-4 animate-pulse-glow"
                  style={{ background: "linear-gradient(135deg, #00F3FF22, #BC13FE22)", border: "1px solid rgba(0,243,255,0.3)" }} />
                <p className="font-outfit text-sm text-gray-500">Loading GPU Plans...</p>
              </div>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {plans.map((plan) => (
                <PricingCard key={plan.id} plan={plan} billingPeriod="monthly" />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Infrastructure Highlights */}
      <section className="py-16" style={{ background: "rgba(10,10,18,0.4)" }}>
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="font-orbitron font-700 text-2xl text-white text-center mb-10">
            Infrastructure <span className="text-gradient">Highlights</span>
          </h2>
          <div className="grid md:grid-cols-3 gap-4">
            {[
              "NVIDIA RTX A6000 Pro",
              "NVIDIA H200 Infrastructure",
              "AMD EPYC Enterprise Platforms",
              "DDR5 ECC Memory",
              "Gen5 NVMe Storage",
              "High-Speed Network",
              "Enterprise Cooling & Power",
              "GPU Virtualization Ready",
              "Managed AI Cloud Ops"
            ].map((feat) => (
              <div key={feat} className="flex items-center gap-3 glass rounded-xl p-4">
                <Shield size={16} color="#00F3FF" className="flex-shrink-0" style={{ filter: `drop-shadow(0 0 6px #00F3FF80)` }} />
                <span className="font-outfit text-sm text-gray-300" style={{ fontWeight: 500 }}>{feat}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

    </div>
  );
}
